import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import * as TimelineService from "_services/timeline-service";
import { formatLiveLocations, convertToDate, hasPresentedAtReception } from "_utils/functions";
import { selectCheckInPausedBookings } from "./timeline-data";
import { selectTimelineBookingsById } from "./timeline-bookings";
import { selectFlightsByBookingKey } from "./timeline-flight-list";

const initialState = {
  timelineAreas: null,
  selectedTimeline: null,
  status: "idle",
  error: null,
  bookingType: ["all"],
  // add timeline area flags
  addTimelineAreaStatus: "idle",
  addTimelineAreaError: null,

  editTimelineAreaStatus: "idle",
  editTimelineAreaError: null,

  deleteTimelineAreaStatus: "idle",
  deleteTimelineAreaError: null,

  airportLocations: [],

  // add Airport Locations flags
  addAirportLocationsStatus: "idle",
  addAirportLocationsError: null,

  // edit Airport Locations flags
  editAirportLocationsStatus: "idle",
  editAirportLocationsError: null,

  // delete Airport Locations flags
  deleteAirportLocationsStatus: "idle",
  deleteAirportLocationsError: null,

  selectedAirportLocation: null,

  // timeline checked in passengers flags
  checkedInPax: null,
  getCheckedInPaxStatus: "idle",
  getCheckedInPaxError: null,

  // timeline assigned passengers flags
  assignedPax: null,
  getAssignedPaxStatus: "idle",
  getAssignedPaxError: null,

  paxLocations: null,

  // SIRAN: SET to TRUE always
  showActivePaxLocations: true,
};

export const getTimelineAreas = createAsyncThunk(
  "timelineAreas/getTimelineAreas",
  async (iata) => {
    return TimelineService.getTimelineAreas(iata);
  }
);
export const addTimelineArea = createAsyncThunk(
  "timelineAreas/addTimelineArea",
  async (data) => {
    return TimelineService.addTimelineArea(data);
  }
);
export const editTimelineArea = createAsyncThunk(
  "timelineAreas/editTimelineArea",
  async (data) => {
    return TimelineService.editTimelineArea(data);
  }
);
export const deleteTimelineArea = createAsyncThunk(
  "timelineAreas/deleteTimelineArea",
  async (data) => {
    return TimelineService.deleteTimelineArea(data);
  }
);

export const getAirportLocation = createAsyncThunk(
  "timelineAreas/getAirportLocation",
  async (iata) => {
    return TimelineService.getAirportLocation(iata);
  }
);

export const addAirportLocation = createAsyncThunk(
  "timelineAreas/addAirportLocation",
  async (data) => {
    return TimelineService.addAirportLocation(data);
  }
);

export const editAirportLocation = createAsyncThunk(
  "timelineAreas/editAirportLocation",
  async (data) => {
    return TimelineService.editAirportLocation(data);
  }
);

export const deleteAirportLocation = createAsyncThunk(
  "timelineAreas/deleteAirportLocation",
  async (data) => {
    return TimelineService.deleteAirportLocation(data);
  }
);

export const getCheckedInPax = createAsyncThunk(
  "timelineAreas/getCheckedInPax",
  async (params) => {
    return TimelineService.getTimelinePax(params);
  }
);

export const getAssignedPax = createAsyncThunk(
  "timelineAreas/getAssignedPax",
  async (params) => {
    return TimelineService.getTimelinePax(params);
  }
);

const timelineAreasSlice = createSlice({
  name: "timelineAreas",
  initialState,
  reducers: {
    reset: (state) => initialState,
    setSelectedTimeline: (state, action) => {
      state.selectedTimeline = action.payload;
    },
    setBookingType: (state, action) => {
      state.bookingType = action.payload;
    },
    resetAddTimelineAreaFlags: (state) => {
      state.addTimelineAreaStatus = "idle";
      state.addTimelineAreaError = null;

      state.editTimelineAreaStatus = "idle";
      state.editTimelineAreaError = null;

      state.deleteTimelineAreaStatus = "idle";
      state.deleteTimelineAreaError = null;
    },

    resetAirportLocationsFlags: (state) => {
      state.addAirportLocationsStatus = "idle";
      state.addAirportLocationsError = null;

      state.editAirportLocationsStatus = "idle";
      state.editAirportLocationsError = null;

      state.deleteAirportLocationsStatus = "idle";
      state.deleteAirportLocationsError = null;
    },
    setSelectedAirportLocation: (state, action) => {
      state.selectedAirportLocation = action.payload;
    },

    setPaxLocations: (state, action) => {
      state.paxLocations = action.payload;
    },

    setShowActivePaxLocations: (state, action) => {
      state.showActivePaxLocations = action.payload;
    },

  },
  extraReducers: {
    [getTimelineAreas.pending]: (state, action) => {
      state.status = "loading";
    },
    [getTimelineAreas.fulfilled]: (state, action) => {
      state.status = "succeeded";
      state.timelineAreas = action.payload;

      // selectedTimeline is persisted, so refresh it from the server copy -
      // otherwise an area edited in admin (e.g. new includeFlights) never reaches
      // a dispatcher who already has that area open
      const freshSelected = Array.isArray(action.payload) && state.selectedTimeline?._id
        ? action.payload.find((area) => area._id === state.selectedTimeline._id)
        : null;
      if (freshSelected) state.selectedTimeline = freshSelected;
    },
    [getTimelineAreas.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.error.message;
    },

    [addTimelineArea.pending]: (state) => {
      state.addTimelineAreaStatus = "loading";
    },
    [addTimelineArea.fulfilled]: (state) => {
      state.addTimelineAreaStatus = "succeeded";
    },
    [addTimelineArea.rejected]: (state, action) => {
      state.addTimelineAreaStatus = "failed";
      state.addTimelineAreaError = action.error.message;
    },
    [editTimelineArea.pending]: (state) => {
      state.editTimelineAreaStatus = "loading";
    },
    [editTimelineArea.fulfilled]: (state) => {
      state.editTimelineAreaStatus = "succeeded";
    },
    [editTimelineArea.rejected]: (state, action) => {
      state.editTimelineAreaStatus = "failed";
      state.editTimelineAreaError = action.error.message;
    },
    [deleteTimelineArea.pending]: (state) => {
      state.deleteTimelineAreaStatus = "loading";
    },
    [deleteTimelineArea.fulfilled]: (state) => {
      state.deleteTimelineAreaStatus = "succeeded";
    },
    [deleteTimelineArea.rejected]: (state, action) => {
      state.deleteTimelineAreaStatus = "failed";
      state.deleteTimelineAreaError = action.error.message;
    },

    [getAirportLocation.fulfilled]: (state, action) => {
      state.airportLocations = action.payload;
    },
    [getAirportLocation.rejected]: (state, action) => {
      state.airportLocations = [];
    },

    [addAirportLocation.pending]: (state) => {
      state.addAirportLocationsStatus = "loading";
    },
    [addAirportLocation.fulfilled]: (state) => {
      state.addAirportLocationsStatus = "succeeded";
    },
    [addAirportLocation.rejected]: (state, action) => {
      state.addAirportLocationsStatus = "failed";
      state.addAirportLocationsError = action.error.message;
    },

    [editAirportLocation.pending]: (state) => {
      state.editAirportLocationsStatus = "loading";
    },
    [editAirportLocation.fulfilled]: (state) => {
      state.editAirportLocationsStatus = "succeeded";
    },
    [editAirportLocation.rejected]: (state, action) => {
      state.editAirportLocationsStatus = "failed";
      state.editAirportLocationsError = action.error.message;
    },

    [deleteAirportLocation.pending]: (state) => {
      state.deleteAirportLocationsStatus = "loading";
    },
    [deleteAirportLocation.fulfilled]: (state) => {
      state.deleteAirportLocationsStatus = "succeeded";
    },
    [deleteAirportLocation.rejected]: (state, action) => {
      state.deleteAirportLocationsStatus = "failed";
      state.deleteAirportLocationsError = action.error.message;
    },


    [getCheckedInPax.pending]: (state) => {
      state.getCheckedInPaxStatus = "loading";
    },
    [getCheckedInPax.fulfilled]: (state, action) => {
      state.getCheckedInPaxStatus = "succeeded";
      state.checkedInPax = action.payload;

    },
    [getCheckedInPax.rejected]: (state, action) => {
      state.getCheckedInPaxStatus = "failed";
      state.getCheckedInPaxError = action.error.message;
    },


    [getAssignedPax.pending]: (state) => {
      state.getAssignedPaxStatus = "loading";
    },
    [getAssignedPax.fulfilled]: (state, action) => {
      state.getAssignedPaxStatus = "succeeded";
      state.assignedPax = action.payload;
    },
    [getAssignedPax.rejected]: (state, action) => {
      state.getAssignedPaxStatus = "failed";
      state.getAssignedPaxError = action.error.message;
    }
  },
});

export const {
  reset,
  setSelectedTimeline,
  setBookingType,
  resetAddTimelineAreaFlags,
  resetAirportLocationsFlags,
  setSelectedAirportLocation,
  setPaxLocations,
  setShowActivePaxLocations
} = timelineAreasSlice.actions;

export const selectAllTimelineAreas = (state) => state.timelineAreas.timelineAreas;
export const selectAllAirportLocations = (state) => state.timelineAreas.airportLocations;

export const selectArrivalsTimelineAreas = (state) => {
  return Array.isFullArray(state.timelineAreas.timelineAreas)
    ? state.timelineAreas.timelineAreas.filter(
      (area) => area?.arrivals === true && area?.departures !== true
    )
    : [];
};
export const selectDeparturesTimelineAreas = (state) => {
  return Array.isFullArray(state.timelineAreas.timelineAreas)
    ? state.timelineAreas.timelineAreas.filter(
      (area) => area?.departures === true && area?.arrivals !== true
    )
    : [];
};

export const selectDeparturesAndArrivalsTimelineAreas = (state) => {
  return Array.isFullArray(state.timelineAreas.timelineAreas) ?
    state.timelineAreas.timelineAreas
    : [];
};
export const selectTimelineAreaStatus = (state) => state.timelineAreas.status;
export const selectTimelineAreaError = (state) => state.contact.error;
export const selectSelectedTimeline = (state) =>
  state.timelineAreas.selectedTimeline;
export const selectBookingType = (state) => state.timelineAreas.bookingType;

export const selectDropdownList = (state) => {
  if (
    state.timelineAreas.timelineAreas !== null &&
    Array.isFullArray(state.timelineAreas.timelineAreas)
  ) {
    return state.timelineAreas.timelineAreas.map((area) => {
      return {
        label: area.title,
        value: area.title,
      };
    });
  } else {
    return [];
  }
};
export const selectAddTimelineAreaStatus = (state) => state.timelineAreas.addTimelineAreaStatus;

export const selectEditTimelineAreaStatus = (state) => state.timelineAreas.editTimelineAreaStatus;

export const selectDeleteTimelineAreaStatus = (state) => state.timelineAreas.deleteTimelineAreaStatus;

/*
export const selectAirportLocationsFilteredBySelectedArea = (state) => {
  if(state.timelineAreas.selectedTimeline === null) return []


  return [];
}
*/

export const selectAirportLocations = (state) => {
  const allLocations = state.timelineAreas.airportLocations;
  const selectedTimeline = state.timelineAreas.selectedTimeline;
  if (Array.isFullArray(allLocations) && selectedTimeline && selectedTimeline.location) {
    const selectedLocationIds = selectedTimeline.location.map(location => location.locationId);

    // Filter airport locations to include only those in the selected timeline
    const filteredLocations = allLocations.filter(location =>
      selectedLocationIds.includes(location._id)
    );

    return filteredLocations;
  }

  // If no timeline is selected, return all locations
  //console.log('All Locations:', allLocations);
  return allLocations || [];
};

export const selectSelectedAirportLocation = (state) => state.timelineAreas.selectedAirportLocation;

export const selectAddAirportLocationsStatus = (state) => state.timelineAreas.addAirportLocationsStatus;

export const selectEditAirportLocationsStatus = (state) => state.timelineAreas.editAirportLocationsStatus;

export const selectDeleteAirportLocationsStatus = (state) => state.timelineAreas.deleteAirportLocationsStatus;

export const selectDropOffLocations = (state) => {
  if (!Array.isFullArray(state.timelineAreas.airportLocations)) return []
  return state.timelineAreas.airportLocations;
  // let tempLocationsArray = state.timelineAreas.airportLocations?.filter((location) => location?.dropOff == true || location?.both == true)
  // if (Array.isFullArray(tempLocationsArray)) return tempLocationsArray
  // else return []
};

export const selectPickUpLocations = (state) => {
  if (!Array.isFullArray(state.timelineAreas.airportLocations)) return []

  // return all locations
  return state.timelineAreas.airportLocations;
  //let tempLocationsArray = state.timelineAreas.airportLocations?.filter((location) => location?.pickUp == true || location?.both == true)
  //if (Array.isFullArray(tempLocationsArray)) return tempLocationsArray
  //else return []
};

export const selectCheckedInPax = (state) => state.timelineAreas.checkedInPax;
export const selectAssignedPax = (state) => state.timelineAreas.assignedPax;

export const selectLiveAirportLocations = createSelector(
  [
    (state) => state.timelineAreas.paxLocations,
    (state) => state?.timelineData?.taskList,
    (state) => state.timelineAreas.showActivePaxLocations,
    (state) => state.timelineAreas.airportLocations,
    (state) => state.timelineBookings.bookingsByFlight,
    (state) => state.user.dbInfo.details.authority
  ],
  (paxLocations, taskList, showActivePaxLocations, airportLocations, bookingsByFlight, iata) => {

    // create list
    let finalPaxLocations = [];

    // filter pax locations by pax type
    const departurePaxLocations = paxLocations?.filter((pax) => pax?.paxType !== "arrival");
    const arrivalPaxLocations = paxLocations?.filter((pax) => pax?.paxType === "arrival");

    if (Array.isFullArray(departurePaxLocations)) finalPaxLocations = [...finalPaxLocations, ...departurePaxLocations];

    // filter arrival pax locations by iata
    if (Array.isFullArray(arrivalPaxLocations)) {
      // get all bookings for the iata
      const timelineBookings = Object?.values(bookingsByFlight)?.flat() || [];
      // get arrival bookings for the iata
      let arrBookings = Array.isFullArray(timelineBookings)
        ? timelineBookings?.filter((booking) => booking?.flightConfirmationDetails?.destination === iata)
        : [];

      // get arrival tasks 
      const arrivalTasks = taskList?.filter((task) => task?.flightBooking);

      // get arrival tasks ids from original booking id
      const arrivalTasksIDS = Array.isFullArray(arrivalTasks)
        ? arrivalTasks?.map((task) => task?.originalBookingId)
        : [];

      // update arrival pax locations with booking doc
      if (Array.isFullArray(arrBookings) && Array.isFullArray(arrivalPaxLocations)) {
        let updatedArrivalPaxLocations = arrivalPaxLocations.map((location) => {
          const booking = arrBookings?.find((booking) => booking?.originalBookingId === location?.paxId);
          if (booking && !arrivalTasksIDS?.includes(location?.paxId)) {
            return {
              ...location,
              paxDoc: { ...booking }
            }
          }
        });
        if (Array.isFullArray(updatedArrivalPaxLocations)) finalPaxLocations = [...finalPaxLocations, ...updatedArrivalPaxLocations];
      }
    }

    // filter alt pickups by isALTPickUp
    const ALTPickUps = Array.isFullArray(taskList) ? taskList?.filter((task) => task?.isALTPickUp === true) : [];

    // add alt pickups to final pax locations
    if (Array.isFullArray(ALTPickUps)) finalPaxLocations = [...finalPaxLocations, ...ALTPickUps];

    // format live locations
    return formatLiveLocations(airportLocations, finalPaxLocations, showActivePaxLocations);
  }
);

// STD comes from the flight row (scheduled = flight doc originalFlightTime). The booking can't give it:
// realtime overwrites departureTime with the live estimate, and the first realtime_history 'from' is
// whatever SITA booked, which is not always the STD (FR4784 28 Oct: booked 11:25Z, STD 08:05Z).
// With no row loaded, fall back to the booking's live departureTime.
const getScheduledDepartureTime = (pax, flightsByBookingKey) => {
  if (!pax) return null;
  const fcd = pax.flightConfirmationDetails;
  if (fcd) {
    const key = `${fcd.carrierCode ?? ''}${fcd.flightNumber ?? ''}_${fcd.origin}_${fcd.destination}_${fcd.departureDate}`;
    const row = flightsByBookingKey[key] ?? flightsByBookingKey[`${key}_BOARDING`];
    if (row && row.scheduled) return row.scheduled;
  }
  return (fcd && fcd.departureTime) ?? (pax.flightInformation && pax.flightInformation.departureTime) ?? null;
};

export const makeSelectPaxAtLocation = (locationId) => createSelector(
  [selectLiveAirportLocations, selectCheckInPausedBookings, selectTimelineBookingsById, selectFlightsByBookingKey],
  (liveLocations, checkInPausedBookings, bookingsById, flightsByBookingKey) => {

    // check location exsists
    if (!locationId || !Array.isArray(liveLocations)) return [];

    // check passengers are at location
    const location = liveLocations.find(loc => loc.locationId === locationId);
    if (!location?.paxArray?.length) return [];
    return location.paxArray.map((pax) => {
      const paxPositionInfo = pax?.paxPositionInfo;
      const isALTPickUp = pax?.isALTPickUp === true;
      // an ALT task keeps paxPositionInfo "Dropped Off" when assigned, the assignment is on the task itself
      const isAssigned = isALTPickUp ? !!(pax.assignedTime && pax.employeeId) : paxPositionInfo?.activity === 'Assigned';
      const employeeId = isAssigned ? ((isALTPickUp ? pax.employeeId : paxPositionInfo?.employeeId) ?? null) : null;
      const employeeName = isAssigned ? ((isALTPickUp ? pax.employeeName : paxPositionInfo?.employeeName) ?? "MISSING") : null;//sometimes employee name is not available, we can not use controller name, then try to find name/title based on employee ID
      const flightNumber = pax?.flightConfirmationDetails?.carrierCode
        ? `${pax?.flightConfirmationDetails?.carrierCode ?? ''}${pax?.flightConfirmationDetails?.flightNumber ?? ''}`
        : `${pax?.flightInformation?.flightNumber ?? ''}`;
      let checkedInTime = pax?.flightBooking
        ? pax?.originalBookingDoc?.arrivalPaxCheckIn?.time ?? null
        : paxPositionInfo?.activity === 'Checked-in' ? paxPositionInfo?.time : null;
      // ALT pickups never check in: the pax has waited since the drop-off (paxPositionInfo.time),
      // which the drop-off path stores as a Firestore Timestamp, so normalise it to ISO
      if (!checkedInTime && pax?.isALTPickUp) checkedInTime = convertToDate(paxPositionInfo?.time);
      if (!checkedInTime) {
        const lastCheckedIn = pax?.paxPositionHistory?.find(history => history?.activity === 'Checked-in');
        checkedInTime = lastCheckedIn ? lastCheckedIn?.time : null;
      }
      const checkInPause = pax?.checkInPause;
      const localPauseEntry = checkInPausedBookings?.[pax?._id] || null;
      const localPausedAt = localPauseEntry?.pausedAt || null;
      const localShiftedCheckInTime = localPauseEntry?.shiftedCheckInTime || null;
      return {
        _id: pax?._id || null,
        name: pax?.name ?? pax?.passengerInformation?.title ?? '-',
        flightNumber: flightNumber,
        destination: pax?.flightConfirmationDetails?.destination ?? pax?.flightInformation?.arrivalAirport ?? '-',
        flightTime: pax?.flightBooking ? pax?.flightConfirmationDetails?.arrivalTime ?? pax?.flightInformation?.arrivalTime ?? null
          : getScheduledDepartureTime(pax, flightsByBookingKey),
        // the local resume time goes stale once the pax is checked in again (Change Loc), so take the later one
        checkedInTime: [localShiftedCheckInTime, checkedInTime].filter(Boolean)
          .sort((a, b) => new Date(b) - new Date(a))[0] ?? null,
        checkInPausedAt: checkInPause?.pausedAt || localPausedAt || null,
        assigned: isAssigned,
        employeeId,
        employeeName,
        mergedFromLocation: pax?.mergedFromLocation ?? null,
        isALTPickUp,
        // ALT drop-offs always get a pickup time from the app; a gate relocation's pickUpTime is only
        // when it was raised, so it counts once a Team Leader/Controller has set one (scheduledPickUp)
        scheduledPickUpTime: isALTPickUp && (!pax.isGateRelocation || pax.scheduledPickUp) ? convertToDate(pax.pickUpTime) : null,
        // collected = the agent actually started the ALT task
        collected: isALTPickUp && !!(pax.startTime || pax.active || pax.completed),
        presented: isALTPickUp ? hasPresentedAtReception(bookingsById[pax.originalALTPaxId]) : null,
        booking: pax,
      };
    });
  }
);

export const selectShowActivePaxLocations = (state) => state.timelineAreas.showActivePaxLocations;

export default timelineAreasSlice.reducer;
